import { useNavigate } from "react-router-dom";
import { Info, Lock, Play } from "lucide-react";
import { DesignationBadge, VerifiedCheck } from "@/components/catalog/Badges";
import { hasFreeEpisode, isCardLocked, useBilling } from "@/context/BillingContext";
import { MyListButton } from "@/components/catalog/MyListButton";
import { titleMeta, TYPE_LABEL } from "@/lib/format";
import { usePlay } from "@/lib/play";

export const CARD_WIDTH = "w-[136px] min-[400px]:w-[148px] sm:w-[176px] lg:w-[212px] 2xl:w-[236px]";

export const TitleCard = ({ title, fluid = false, rank }) => {
  const navigate = useNavigate();
  const play = usePlay();
  const { ent, openUpgrade } = useBilling();
  const locked = isCardLocked(ent, title);
  return (
    <div
      role="link"
      tabIndex={0}
      data-testid={`title-card-${title.slug}`}
      onClick={() => navigate(`/title/${title.slug}`)}
      onKeyDown={(e) => e.key === "Enter" && navigate(`/title/${title.slug}`)}
      className={`group relative flex-none cursor-pointer snap-start outline-none ${fluid ? "w-full" : CARD_WIDTH}`}
    >
      <div className="relative aspect-[2/3] overflow-hidden rounded-lg border border-[#D4AF37]/20 bg-[#0B0B0D] shadow-[0_10px_28px_rgba(0,0,0,0.7)] transition-[transform,border-color,box-shadow] duration-500 ease-out active:scale-[0.97] md:group-hover:scale-[1.06] md:group-hover:border-[#FFD86A]/80 md:group-hover:shadow-[0_18px_50px_rgba(0,0,0,0.9),0_0_22px_rgba(212,175,55,0.22)] group-focus-visible:border-[#FFD86A]">
        <img src={title.poster_url} alt={title.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-700 md:group-hover:scale-105" />
        {title.designation && <span className="absolute left-1.5 top-1.5"><DesignationBadge value={title.designation} testId={`card-designation-${title.slug}`} /></span>}
        {locked && <span data-testid={`card-lock-${title.slug}`} title="Premium — upgrade to watch" className="absolute right-1.5 bottom-1.5 md:bottom-auto md:top-1.5 grid h-7 w-7 place-items-center rounded-full border border-[#FFD86A]/60 bg-[#050505]/80 text-[#FFD86A] backdrop-blur"><Lock className="h-3.5 w-3.5" /></span>}
        {hasFreeEpisode(ent, title) && <span data-testid={`card-free-ep-${title.slug}`} className="absolute right-1.5 bottom-1.5 md:bottom-auto md:top-1.5 rounded-full bg-[#050505]/80 px-1.5 py-0.5 text-[0.55rem] font-bold tracking-[0.15em] text-[#7CF7FF] backdrop-blur">EP 1 FREE</span>}
        {rank && <span className="absolute right-2 top-1 font-display text-4xl font-black gold-text">{rank}</span>}
        <div className="absolute inset-x-0 bottom-0 hidden md:block bg-gradient-to-t from-[#050505] via-[#050505]/90 to-transparent p-3 pt-16 transition-[opacity,transform] duration-300 opacity-0 translate-y-3 group-hover:opacity-100 group-hover:translate-y-0">
          <p className="text-[0.72rem] font-semibold text-[#F5C451]">{TYPE_LABEL[title.type]} · {titleMeta(title)}</p>
          <p className="mt-1 text-[0.7rem] text-white/60 truncate">{title.genres?.join(" · ")}</p>
          <div className="mt-2.5 flex items-center gap-2">
            <button
              data-testid={`card-play-btn-${title.slug}`}
              onClick={(e) => { e.stopPropagation(); if (locked) openUpgrade(title); else play(title.slug); }}
              aria-label={`Play ${title.title}`}
              className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-[#FFE9A3] to-[#D4AF37] text-black hover:scale-110 transition-transform"
            >
              {locked ? <Lock className="h-4 w-4" /> : <Play className="h-4 w-4 fill-current" />}
            </button>
            <MyListButton title={title} variant="icon" testId={`card-my-list-btn-${title.slug}`} />
            <button
              data-testid={`card-info-btn-${title.slug}`}
              onClick={(e) => { e.stopPropagation(); navigate(`/title/${title.slug}`); }}
              aria-label="More info"
              className="ml-auto grid h-9 w-9 place-items-center rounded-full border border-[#D4AF37]/50 bg-[#050505]/60 text-[#F5C451] hover:border-[#FFD86A] hover:text-[#FFD86A] transition-colors"
            >
              <Info className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
      <h3 className="mt-2 px-0.5 font-display text-[0.8rem] sm:text-[0.86rem] font-semibold tracking-[0.04em] text-white/85 truncate transition-colors md:group-hover:text-[#FFD86A] flex items-center gap-1"><span className="truncate">{title.title}</span>{title.creator_verified && <VerifiedCheck className="h-3.5 w-3.5" testId={`card-verified-${title.slug}`} />}</h3>
    </div>
  );
};
